import { useCallback, useEffect, useRef, useState } from 'react';
import { message } from 'antd';
import { apiGet, parseJsonResponse } from '../api/apiClient';
import { getInitialValuesFormular } from '../utils/formUtils';
import { isTextFieldActive } from '../utils/scroll';
import { subscribeGutachtenUpdated } from '../utils/gutachtenTabSync';

/** Fallback-Polling nur wenn kein anderes Signal greift (Multi-Tab sonst über BroadcastChannel). */
const POLL_FALLBACK_MS = 120000;

/** Entprellt Refetch bei Tab-Fokus und Cross-Tab-Sync (weniger Burst-Requests). */
const REFETCH_DEBOUNCE_MS = 400;

const jumpToAnchor = () => {
  const hash = window.location.hash.slice(1);
  if (!hash) return;
  const el = document.querySelector(`a[href="#${hash}"]`);
  if (el) el.click();
};

/**
 * Kapselt Datenladen, Tab-Sync, selteneres Polling und Anchor-Jump für Gutachten-Komponenten.
 * Hintergrund-Refetches (nach Speichern, Tab-Sync, Polling) laufen mit silent=true ohne Vollbild-Loading.
 *
 * @param {string} gutachtenID
 * @param {{ setIsLoading, updateTrigger, saveIcon, onLoad }} options
 *   onLoad(data) - Callback für komponentenspezifische Post-Load-Logik
 */
export const useGutachtenData = (gutachtenID, { setIsLoading, updateTrigger, saveIcon, onLoad } = {}) => {
  const dataContentRef = useRef(null);
  const [initialValues, setInitialValues] = useState({});
  const [enumerationValues, setEnumerationValues] = useState(null);

  const saveIconRef = useRef(saveIcon);
  useEffect(() => {
    saveIconRef.current = saveIcon;
  }, [saveIcon]);

  const gutachtenIdRef = useRef(String(gutachtenID));
  useEffect(() => {
    gutachtenIdRef.current = String(gutachtenID);
  }, [gutachtenID]);

  const onLoadRef = useRef(onLoad);
  useEffect(() => {
    onLoadRef.current = onLoad;
  }, [onLoad]);

  const abortRef = useRef(null);
  const silentAbortRef = useRef(null);
  const fetchSeqRef = useRef(0);
  const silentFetchSeqRef = useRef(0);
  const fetchDataRef = useRef(
    /** @type {(jump?: boolean, silent?: boolean) => Promise<void>} */ async () => {}
  );
  const refetchDebounceTimerRef = useRef(null);
  const isFirstFetchForGutachtenRef = useRef(true);

  const fetchData = useCallback(
    async (jumptoanchor = true, silent = false) => {
      const runWithSignal = async (ac, seq, seqRef) => {
        const [gutachtenRes, enumRes] = await Promise.all([
          apiGet(`/helper/acl/gutachten/${gutachtenID}?populate=deep`, { signal: ac.signal }),
          apiGet('/gutachten/enumvalues', { signal: ac.signal }),
        ]);
        if (seq !== seqRef.current) return;

        const [data, enums] = await Promise.all([
          parseJsonResponse(gutachtenRes),
          parseJsonResponse(enumRes),
        ]);
        if (seq !== seqRef.current) return;

        dataContentRef.current = data;
        setInitialValues(getInitialValuesFormular(data));
        setEnumerationValues(enums);
        onLoadRef.current?.(data);
      };

      if (silent) {
        silentAbortRef.current?.abort();
        const ac = new AbortController();
        silentAbortRef.current = ac;
        const seq = ++silentFetchSeqRef.current;
        try {
          await runWithSignal(ac, seq, silentFetchSeqRef);
        } catch (error) {
          if (error?.name === 'AbortError') return;
          if (seq !== silentFetchSeqRef.current) return;
          message.error('Fehler beim Laden der Gutachten-Daten: ' + (error?.message || error));
        }
        return;
      }

      silentAbortRef.current?.abort();
      abortRef.current?.abort();
      const ac = new AbortController();
      abortRef.current = ac;
      const seq = ++fetchSeqRef.current;

      setIsLoading(true);
      try {
        await runWithSignal(ac, seq, fetchSeqRef);
      } catch (error) {
        if (error?.name === 'AbortError') return;
        if (seq !== fetchSeqRef.current) return;
        message.error('Fehler beim Laden der Gutachten-Daten: ' + (error?.message || error));
      } finally {
        if (seq === fetchSeqRef.current) {
          if (jumptoanchor) jumpToAnchor();
          setIsLoading(false);
        }
      }
    },
    [gutachtenID, setIsLoading]
  );

  fetchDataRef.current = fetchData;

  const scheduleBackgroundRefetch = useCallback(() => {
    if (refetchDebounceTimerRef.current) {
      clearTimeout(refetchDebounceTimerRef.current);
    }
    refetchDebounceTimerRef.current = setTimeout(() => {
      refetchDebounceTimerRef.current = null;
      fetchDataRef.current(false, true);
    }, REFETCH_DEBOUNCE_MS);
  }, []);

  useEffect(
    () => () => {
      abortRef.current?.abort();
      silentAbortRef.current?.abort();
      if (refetchDebounceTimerRef.current) {
        clearTimeout(refetchDebounceTimerRef.current);
        refetchDebounceTimerRef.current = null;
      }
    },
    []
  );

  useEffect(() => {
    isFirstFetchForGutachtenRef.current = true;
  }, [gutachtenID]);

  useEffect(() => {
    const silent = !isFirstFetchForGutachtenRef.current;
    isFirstFetchForGutachtenRef.current = false;
    fetchData(saveIconRef.current ? false : true, silent);
  }, [updateTrigger, fetchData]);

  useEffect(() => {
    const unsub = subscribeGutachtenUpdated((id) => {
      if (id === gutachtenIdRef.current) {
        scheduleBackgroundRefetch();
      }
    });
    return unsub;
  }, [scheduleBackgroundRefetch]);

  useEffect(() => {
    const onVis = () => {
      if (document.visibilityState === 'visible' && !isTextFieldActive()) {
        scheduleBackgroundRefetch();
      }
    };
    document.addEventListener('visibilitychange', onVis);
    return () => document.removeEventListener('visibilitychange', onVis);
  }, [scheduleBackgroundRefetch]);

  useEffect(() => {
    const id = setInterval(() => {
      if (document.visibilityState === 'visible' && !isTextFieldActive()) {
        fetchDataRef.current(false, true);
      }
    }, POLL_FALLBACK_MS);
    return () => clearInterval(id);
  }, []);

  return { dataContentRef, initialValues, setInitialValues, enumerationValues };
};
